import React, { FC, ReactNode, useCallback, useRef, useState } from 'react';
import type { FormValues, FormViewerHandle } from '@wix/form-viewer/widget';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { allowsFutureStartDate } from '@wix/pricing-plans-utils';
import { useStyles } from '@wix/tpa-settings/react';
import { Text, Button, ButtonPriority, ThreeDotsLoader, Divider, HoverStyles } from '@wix/wix-ui-tpa/cssVars';
import { useTranslation } from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import { withClientSideRenderMark } from '../../../contexts/ClientSideRenderMark';
import { Toast } from '../../PackagePicker/Widget/Toast';
import componentStyles from '../stylesParams';
import { PlanCustomizationProps } from '../types';
import { AdditionalDetailsLoader } from './AdditionalDetails/AdditionalDetailsLoader';
import { EditorPreviewNotification } from './EditorPreviewNotification/EditorPreviewNotification';
import { st, classes } from './PlanCustomizationWidget.st.css';
import { PlanPreview } from './PlanPreview/PlanPreview';
import { PlanStartDatePickerLoader } from './PlanStartDatePicker/PlanStartDatePickerLoader';

const LazyPlanStartDatePicker = loadable(() => import('./PlanStartDatePicker/PlanStartDatePicker'), {
  fallback: <PlanStartDatePickerLoader />,
});
const LazyAdditionalDetails = loadable(() => import('./AdditionalDetails/AdditionalDetails'), {
  fallback: <AdditionalDetailsLoader />,
});

const ProductPageWidget: React.FC<PlanCustomizationProps> = (props) => {
  const styles = useStyles();
  const { t } = useTranslation();

  if (props.status === 'PAGE_LOADING') {
    return null;
  }

  const hasShadow = styles.get(componentStyles.planFormEnableShadow);
  const hasBorder = !!styles.get(componentStyles.planFormBorderWidth);

  return (
    <>
      <EditorPreviewNotification />
      {props.statusMessage && (
        <Toast informational={props.status !== 'ERROR'} onClose={() => props.clearStatusMessage()}>
          {props.statusMessage}
        </Toast>
      )}
      <div className={classes.root}>
        {styles.get(componentStyles.showPlanCard) && (
          <aside className={classes.previewContainer}>
            <PlanPreview plan={props.plan} />
          </aside>
        )}
        <main
          className={st(classes.contentContainer, {
            'with-padding': hasShadow || hasBorder,
            'with-shadow': hasShadow,
          })}
        >
          {styles.get(componentStyles.showPageTitle) && (
            <header className={classes.header}>
              <Text
                className={classes.pageTitle}
                tagName={
                  styles.get(componentStyles.pageTitleFont).htmlTag ??
                  styles.getDefaultValue(componentStyles.pageTitleFont).htmlTag
                }
              >
                {t('plan-customization.widget.form-title')}
              </Text>
              <Divider className={classes.headerDivider} />
            </header>
          )}
          <ProductCustomizationForm {...props} />
        </main>
      </div>
    </>
  );
};

const ProductCustomizationForm: FC<PlanCustomizationProps> = ({
  plan,
  status,
  formControllerStatus,
  proceedToPayment,
  minStartDate,
  maxStartDate,
  initialStartDate,
  disableAllDates,
  noAvailableDatesMessage,
  isCartEnabled,
}) => {
  const formViewerRef = useRef<FormViewerHandle>(null);
  const [formValues, setFormValues] = useState<FormValues>({});

  const getInitialStartDate = useCallback(() => {
    return disableAllDates ? undefined : new Date(initialStartDate ?? minStartDate ?? Date.now());
  }, [disableAllDates, initialStartDate, minStartDate]);

  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(getInitialStartDate());
  const { t } = useTranslation();

  const isCheckoutLoading = status === 'CHECKOUT_LOADING';
  const hasConnectedForm = !!plan.formId;

  const [startDateLoaded, setStartDateLoaded] = useState(false);
  const [formsComponentLoaded, setFormsComponentLoaded] = useState(false);

  const onContinueClick = async () => {
    // TODO: What to do if (hasConnectedForm && !formViewerRef.current) ?
    if (hasConnectedForm && formViewerRef.current) {
      const isValid = await formViewerRef.current?.validate();

      if (!isValid) {
        return;
      }
    }

    proceedToPayment({
      startDate:
        allowsFutureStartDate(plan.pricingVariants![0]) && customStartDate && !disableAllDates
          ? customStartDate.toString()
          : null,
      formValues: hasConnectedForm ? formValues : null,
    });
  };

  const isButtonDisabled = () => {
    if (isCheckoutLoading) {
      return true;
    }

    if (allowsFutureStartDate(plan.pricingVariants![0]) && !startDateLoaded) {
      return true;
    }

    if (allowsFutureStartDate(plan.pricingVariants![0]) && disableAllDates) {
      return true;
    }

    if (hasConnectedForm && !formsComponentLoaded) {
      return true;
    }

    return false;
  };

  return (
    <div>
      {allowsFutureStartDate(plan.pricingVariants![0]) && (
        <LazyPlanStartDatePicker
          className={classes.datePicker}
          value={customStartDate}
          onChange={(date) => setCustomStartDate(date)}
          min={minStartDate}
          max={maxStartDate}
          disableAllDates={disableAllDates}
          noAvailableDatesMessage={noAvailableDatesMessage}
          onLoad={() => setStartDateLoaded(true)}
        />
      )}
      {hasConnectedForm && (
        <LazyAdditionalDetails
          plan={plan}
          formValues={formValues}
          setFormValues={setFormValues}
          ref={formViewerRef}
          formControllerStatus={formControllerStatus}
          onLoad={() => setFormsComponentLoaded(true)}
        />
      )}
      <ContinueButton loading={isCheckoutLoading} onClick={onContinueClick} disabled={isButtonDisabled()}>
        {isCartEnabled
          ? t('plan-customization.widget.button-text-continue-to-cart')
          : t('plan-customization.widget.button-text')}
      </ContinueButton>
      <Text tagName="p" className={classes.disclaimer}>
        {t('plan-customization.widget.disclaimer')}
      </Text>
    </div>
  );
};

const ContinueButton: FC<{ loading: boolean; children: ReactNode; onClick: () => void; disabled: boolean }> = ({
  loading,
  onClick,
  children,
  disabled,
}) => {
  return (
    <Button
      data-hook={VIEWER_HOOKS.PLAN_CUSTOMIZATION_GO_TO_CHECKOUT_BUTTON}
      hoverStyle={HoverStyles.underline}
      className={st(classes.ctaButton, { loading })}
      fullWidth
      upgrade
      priority={ButtonPriority.primary}
      onClick={onClick}
      disabled={loading || disabled}
    >
      <span className={classes.label}>{children}</span>
      {loading && (
        <div className={classes.loaderContainer}>
          <ThreeDotsLoader className={classes.loader} />
        </div>
      )}
    </Button>
  );
};

export default withClientSideRenderMark(ProductPageWidget);
